import React, { useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import AppDropdown from "../../../components/AppDropdown/AppDropdown";
import * as hrmsService from "../../../services/hrmsService";
import { monthBounds, shiftMonth } from "../leaveCalendar";
import { useMasterOptions } from "../hrForm";
import { musterSheet } from "../timeHelpers";
import { EmptyState, LoadState, SecondaryButton } from "../hrComponents";
import { attendanceStatus, cardStyle, formatMinutes, thStyle, tdStyle, todayYmd, useLoad, addDaysYmd } from "../hrUi";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// ATT-07: the monthly muster roll. One row per person, a coloured code per day, and the month's totals.
export default function Muster() {
  const [month, setMonth] = useState(monthBounds(todayYmd()).from);
  const [department, setDepartment] = useState("ALL");
  const { from, to } = monthBounds(month);
  const options = useMasterOptions(["departments"]);
  const { data, loading, error, reload } = useLoad(() => hrmsService.getMuster({ from, to, department_id: department === "ALL" ? undefined : department }), [from, to, department]);
  const [y, m] = month.split("-").map(Number);
  const days = [];
  for (let d = from; d <= to; d = addDaysYmd(d, 1)) days.push(d);
  const cell = { width: 26, minWidth: 26, height: 26, textAlign: "center", fontSize: 10, padding: 0, borderBottom: "1px solid #F3F4F6", fontWeight: 600 };

  const exportSheet = () => {
    try {
      const ws = XLSX.utils.aoa_to_sheet(musterSheet(data, (s) => attendanceStatus(s).code));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Muster roll");
      XLSX.writeFile(wb, `muster-${from.slice(0, 7)}.xlsx`);
    } catch (e) {
      toast.error("Could not create the spreadsheet");
    }
  };

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <button type="button" className="btn btn-outline-secondary btn-sm" aria-label="Previous month" onClick={() => setMonth(shiftMonth(month, -1))}>
          ‹
        </button>
        <strong style={{ minWidth: 140, textAlign: "center" }}>
          {MONTHS[m - 1]} {y}
        </strong>
        <button type="button" className="btn btn-outline-secondary btn-sm" aria-label="Next month" onClick={() => setMonth(shiftMonth(month, 1))}>
          ›
        </button>
        <div style={{ width: 200 }}>
          <AppDropdown value={department} options={[{ label: "All departments", value: "ALL" }, ...(options.departments || [])]} onChange={(e) => setDepartment(e.value)} />
        </div>
        <SecondaryButton onClick={exportSheet} disabled={!data || !data.people.length} className="btn btn-outline-secondary px-3 ms-auto">
          Export to Excel
        </SecondaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload} rows={6}>
        {!data || data.people.length === 0 ? (
          <EmptyState title="No attendance for this month" />
        ) : (
          <>
            <div style={{ ...cardStyle, overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", minWidth: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, position: "sticky", left: 0, zIndex: 1 }}>Employee</th>
                    {days.map((d) => (
                      <th key={d} style={{ ...cell, background: "#F9FAFB" }}>
                        {Number(d.slice(8))}
                      </th>
                    ))}
                    {["P", "A", "LV", "Late", "OT"].map((h) => (
                      <th key={h} style={{ ...thStyle, textAlign: "center", padding: "8px 10px" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.people.map((p) => (
                    <tr key={p.employee_id}>
                      <td style={{ ...tdStyle, position: "sticky", left: 0, background: "#fff", whiteSpace: "nowrap" }}>{p.full_name}</td>
                      {days.map((d) => {
                        const day = p.days[d];
                        const st = day ? attendanceStatus(day.status) : null;
                        return (
                          <td key={d} title={st ? `${st.label}${day.net_minutes ? ` · ${formatMinutes(day.net_minutes)}` : ""}` : undefined} style={{ ...cell, background: st ? st.bg : "transparent", color: st ? st.color : "#D1D5DB" }}>
                            {st ? st.code : "·"}
                          </td>
                        );
                      })}
                      <td style={{ ...tdStyle, textAlign: "center", padding: "8px 10px" }}>{p.summary.present + p.summary.half_days * 0.5}</td>
                      <td style={{ ...tdStyle, textAlign: "center", padding: "8px 10px", color: p.summary.absent ? "#991B1B" : undefined }}>{p.summary.absent}</td>
                      <td style={{ ...tdStyle, textAlign: "center", padding: "8px 10px" }}>{p.summary.on_leave}</td>
                      <td style={{ ...tdStyle, textAlign: "center", padding: "8px 10px" }}>{p.summary.late_marks_counted}</td>
                      <td style={{ ...tdStyle, textAlign: "center", padding: "8px 10px" }}>{formatMinutes(p.summary.ot_minutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.locked_through ? <div style={{ fontSize: 12, color: "#6B7280", marginTop: 8 }}>Attendance is locked up to {data.locked_through}.</div> : null}
          </>
        )}
      </LoadState>
    </>
  );
}
